import type { ModelAiClientRecord } from '@app/api/Leads/types';
import type { PayType } from '@app/api/PaymentAccounts/types';
import type { IBaseQueryParams } from '@app/services/rtkQuery/baseQuery';
import type {
  NActionsHistory,
  NClientServices,
  NFiles,
  NRecords,
  NSchedule,
  NSmallGlobalEntities,
  NTags,
  TBranchUser,
} from '@app/types';

export enum RecordsEndpoints {
  GetRecordFilterLists = 'GetRecordFilterLists',
  SearchRecords = 'SearchRecords',
  GetRecordsSearchResult = 'GetRecordsSearchResult',
  GetRecordsSearchResultFull = 'GetRecordsSearchResultFull',
  GetRecordById = 'GetRecordById',
  ExportRecordsToExcel = 'ExportRecordsToExcel',
  ExportRecordsToExcelWithoutGrouping = 'ExportRecordsToExcelWithoutGrouping',
  ExportRecordsToExcelByIds = 'ExportRecordsToExcelByIds',
  DeleteRecords = 'DeleteRecords',
  PrepareRecordCreation = 'PrepareRecordCreation',
  UpdateRecord = 'UpdateRecord',
  CheckRecordTimes = 'CheckRecordTimes',
  CheckRecordMove = 'CheckRecordMove',
  GetRecordHistory = 'GetRecordHistory',
  GetEmployeesRecords = 'GetEmployeesRecords',
  GetRecordsJournalLoad = 'GetRecordsJournalLoad',
  GetEmployeeRecords = 'GetEmployeeRecords',
  GetEmployeesRecordsOnDates = 'GetEmployeesRecordsOnDates',
  GetEmployeeRecordsTip = 'GetEmployeeRecordsTip',
  DeleteRecordFromJournal = 'DeleteRecordFromJournal',
  GetEmployeesByServices = 'GetEmployeesByServices',
  SetRecordsTags = 'SetRecordsTags',
  GetClientRecords = 'GetClientRecords',
  GetRecordReceiptPdf = 'GetRecordReceiptPdf',
  GetRecordPayments = 'GetRecordPayments',
  ExportRecordsByChainToExcel = 'ExportRecordsByChainToExcel',
  ExportRecordsByOrganizationToExcel = 'ExportRecordsByOrganizationToExcel',
  ImportExcel = 'ImportExcel',
  StopImportExcel = 'StopImportExcel',
  CreateRecordByAiRequestId = 'CreateRecordByAiRequestId',
  GetRecordProperties = 'GetRecordProperties',
  SetRecordProperties = 'SetRecordProperties',
}

export interface GetRecordFilterListsRequest {
  ActiveBranchId: number | null;
  logs?: IBaseQueryParams['logs'];
}

export interface SearchRecordsRequest {
  ActiveBranchId: number | null;
  data: NRecords.ModelRecordSearchFilter;
  logs?: IBaseQueryParams['logs'];
}

export interface GetRecordsSearchResultRequest {
  searchId: number;
  page: number;
  rowsPerPage: number;
  ActiveBranchId: number | null;
}

export type GetRecordsSearchResultFullRequest = GetRecordsSearchResultRequest;

export interface GetRecordByIdRequest {
  recordId: number;
  ActiveBranchId: number | null;
}

export interface ExportRecordsToExcelRequest {
  searchId: number;
  ActiveBranchId: number | null;
}

export type ExportRecordsToExcelWithoutGroupingRequest =
  ExportRecordsToExcelRequest;

export interface ExportRecordsToExcelByIdsRequest {
  ActiveBranchId: number | null;
  data: number[];
}

export interface DeleteRecordsRequest {
  ActiveBranchId: number | null;
  data: number[];
}

export interface PrepareRecordCreationRequest {
  employeeId: number;
  dateTime: string;
  recordId: number;
  ActiveBranchId: number | null;
}

export interface UpdateRecordRequest {
  ActiveBranchId: number | null;
  data: NRecords.ModelRecord;
}

export interface CheckRecordTimesRequest {
  recordId: number | null;
  employeeId: number | null;
  branchId: number | null;
  timeS: string;
  timeE: string;
  ActiveBranchId: number | null;
}

export interface CheckRecordMoveRequest {
  ActiveBranchId: number | null;
  data: NRecords.ModelRecordMoveCheck;
}

export interface GetRecordHistoryRequest {
  recordId: number;
  ActiveBranchId: number | null;
}

export interface GetEmployeesRecordsRequest {
  date: string;
  ActiveBranchId: number | null;
  logs?: IBaseQueryParams['logs'];
}

export interface GetRecordsJournalLoadRequest {
  dateS: string;
  dateE: string;
  ActiveBranchId: number | null;
}

export interface GetEmployeeRecordsRequest {
  employeeId: number;
  dateS: string;
  dateE: string;
  ActiveBranchId: number | null;
}

export interface GetEmployeesRecordsOnDatesRequest {
  dateS: string;
  dateE: string;
  ActiveBranchId: number | null;
  data: number[];
}

export interface GetEmployeeRecordsTipRequest {
  employeeId: number;
  date: string;
  ActiveBranchId: number | null;
}

export interface DeleteRecordFromJournalRequest {
  recordId: number;
  ActiveBranchId: number | null;
}

export interface GetEmployeesByServicesRequest {
  branchId: number;
  date: string;
  recordId: number;
  ActiveBranchId: number | null;
  data: number[];
  logs?: IBaseQueryParams['logs'];
}

export interface SetRecordsTagsRequest {
  ActiveBranchId: number | null;
  data: NRecords.ModelRecordsTags;
}

export interface GetClientRecordsRequest {
  clientId: number;
  ActiveBranchId: number | null;
}

export interface GetRecordReceiptPdfRequest {
  recordId: number;
  ActiveBranchId: number | null;
}

export interface GetRecordPaymentsRequest {
  ActiveBranchId: number | null;
  recordIds: number[];
}

export interface ModelPrepareRecord {
  BranchId: number;
  LeadId: number | null;
  Branch: string | null;
  EmployeeId: number;
  // Организация включила окно тегов после сохранения новой записи (us/11144).
  IsRecordTagsWindow: boolean;
  ClientServices: NClientServices.ModelClientServiceLite[] | null;
  WorkSchedules: NSchedule.ModelWorkSchedule[] | null;
  Tags: NTags.ModelTag[] | null;
  BranchUsers: TBranchUser[] | null;
  BranchEmployees: TBranchUser[] | null;
  Warehouses: NSmallGlobalEntities.ModelItem[] | null;
}

// Тип клиента и тип записи считает бэк при создании записи (us/11144):
// ClientType — 'new' | 'old', у выбранного типа записи IsSeleted = true
// (опечатка в имени поля — на стороне контракта). Теги пользователь выбирает
// сам: выбранный помечается IsSelected.
export interface ModelRecordTypeValue {
  Key: string | null;
  Name: string | null;
  IsSeleted: boolean;
}

export interface ModelRecordProperties {
  RecordId: number;
  ClientType: string | null;
  RecordTypes: ModelRecordTypeValue[] | null;
  Tags: NTags.ModelTag[] | null;
}

export interface ModelJournalDayLoad {
  Date: string;
  LoadPercent: number;
  CountEmployees: number;
}

export interface ModelRecordsJournalLoad {
  BranchId: number;
  DateS: string;
  DateE: string;
  LoadDays: ModelJournalDayLoad[] | null;
}

export interface ModelRecordSearchResultFull {
  Id: number;
  Filter: NRecords.ModelRecordSearchFilter;
  Records: NRecords.ModelRecord[] | null;
  PageN: number;
  CountPages: number;
  CountRecords: number;
}

export interface ModelRecordPayment {
  RecordId: number;
  PaymentId: number;
  Timestamp: string;
  TimeCreated: string;
  UserId: number;
  UserName: string | null;
  IsPrepay: boolean;
  Amount: number;
  PaymentType: PayType;
  PaymentAccountId: number;
  PaymentAccount: string | null;
  Comment: string | null;
  IsFromBalance: boolean;
  IsSuccess: boolean;
  Status:
    | 'succeeded'
    | 'waiting_for_capture'
    | 'canceled'
    | 'pending'
    | 'awaiting_email';
  PaymentMethod: string;
}

// Payload WS-события об обновлении платежа записи: тот же ModelRecordPayment,
// но сервер шлёт ключи в lowerCamelCase (фронт капитализирует capitalizeKeys).
export interface ModelRecordPaymentLowerCase {
  recordId: number;
  paymentId: number;
  timestamp: string;
  timeCreated: string;
  userId: number;
  userName: string | null;
  isPrepay: boolean;
  amount: number;
  paymentType: PayType;
  paymentAccountId: number;
  paymentAccount: string | null;
  comment: string | null;
  isFromBalance: boolean;
  isSuccess: boolean;
}

export interface Int32ItemsFilter {
  OuterInclude: boolean;
  InnerInclude?: boolean | null;
  ItemIds?: number[] | null;
}

export interface ModelExportRecordsByChain {
  DateCreateS?: string | null;
  DateCreateE?: string | null;
  DateVisitS?: string | null;
  DateVisitE?: string | null;
  RecordTagsFilters?: Int32ItemsFilter[] | null;
  ClientTagsFilters?: Int32ItemsFilter[] | null;
  Statuses?: string[] | null;
}

export interface ExportRecordsByChainToExcelRequest {
  ActiveBranchId: number | null;
  data: ModelExportRecordsByChain;
}

export interface ExportRecordsByOrganizationToExcelRequest {
  ActiveBranchId: number | null;
  data: ModelExportRecordsByChain;
}

export interface ImportExcelRequest {
  ActiveBranchId: number | null;
  data: string;
}

export interface StopImportExcelRequest {
  ActiveBranchId: number | null;
}

export interface CreateRecordByAiRequestIdRequest {
  ActiveBranchId: number | null;
  // RequestId из ответа GetRecordSlotForClient — тело запроса это просто
  // JSON-строка с ним, без обёртки в объект.
  data: string;
}

export type GetRecordFilterListsResponse = NRecords.ModelRecordFilterLists;
export type SearchRecordsResponse = NRecords.ModelRecordSearchResult;
export type GetRecordsSearchResultResponse = NRecords.ModelRecordSearchResult;
export type GetRecordsSearchResultFullResponse = ModelRecordSearchResultFull;
export type GetRecordByIdResponse = NRecords.ModelRecord;
export type ExportRecordsToExcelResponse = NFiles.ModelFile;
export type ExportRecordsToExcelWithoutGroupingResponse = NFiles.ModelFile;
export type ExportRecordsToExcelByIdsResponse = NFiles.ModelFile;
export type DeleteRecordsResponse = void;
export type PrepareRecordCreationResponse = ModelPrepareRecord;
export type UpdateRecordResponse = NRecords.ModelRecord;
export type CheckRecordTimesResponse = NRecords.ModelRecordCheck;
export type CheckRecordMoveResponse = NRecords.ModelRecordMoveCheck;
export type GetRecordHistoryResponse = NActionsHistory.ModelActionHistory[];
export type GetEmployeesRecordsResponse = NRecords.ModelEmployeeRecords[];
export type GetRecordsJournalLoadResponse = ModelRecordsJournalLoad;
export type GetEmployeeRecordsResponse = NRecords.ModelEmployeeRecords[];
export type GetEmployeesRecordsOnDatesResponse = NRecords.ModelRecordsOnDates[];
export type GetEmployeeRecordsTipResponse = NRecords.ModelRecordTip[];
export type DeleteRecordFromJournalResponse = void;
export type GetEmployeesByServicesResponse = NRecords.ModelEmployeeByService[];
export type SetRecordsTagsResponse = void;

export interface GetRecordPropertiesRequest {
  ActiveBranchId: number | null;
  recordId: number;
}

export type GetRecordPropertiesResponse = ModelRecordProperties;

export interface SetRecordPropertiesRequest {
  ActiveBranchId: number | null;
  data: ModelRecordProperties;
}

export type SetRecordPropertiesResponse = ModelRecordProperties;
export type GetClientRecordsResponse = NRecords.ModelClientRecord[];
export type GetRecordReceiptPdfResponse = NFiles.ModelFile;
export type GetRecordPaymentsResponse = ModelRecordPayment[];
export type ExportRecordsByChainToExcelResponse = NFiles.ModelFile;
export type ExportRecordsByOrganizationToExcelResponse = NFiles.ModelFile;
export type ImportExcelResponse = void;
export type StopImportExcelResponse = void;
export type CreateRecordByAiRequestIdResponse = ModelAiClientRecord;
